import { useState, useRef, useEffect } from 'react';
import { FaTimes, FaUser, FaPlus } from 'react-icons/fa';

interface ExternalUser {
  first_name: string;
  last_name: string;
  email?: string;
}

interface SmartAttendeeInputProps {
  users: any[];
  allUsers?: ProjectUserInterface[];
  selectedAttendees: (string | ExternalUser)[];
  onAttendeesChange: (attendees: (string | ExternalUser)[]) => void;
  placeholder?: string;
  maxAttendees?: number;
  allowExternal?: boolean;
}

export const SmartAttendeeInput = ({
  users,
  allUsers,
  selectedAttendees,
  onAttendeesChange,
  placeholder = "Type name to add...",
  maxAttendees = 10,
  allowExternal = true
}: SmartAttendeeInputProps) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter users based on input
  useEffect(() => {
    if (inputValue.trim().length > 0) {
      const searchTerm = inputValue.toLowerCase();
      const filtered = users.filter(user => {
        const fullName = `${user.user?.first_name || user.first_name} ${user.user?.last_name || user.last_name}`.toLowerCase();
        const userId = user.user_id || user.user?.id || user.id;
        // Don't show already selected users
        const isAlreadySelected = selectedAttendees.some(attendee => 
          typeof attendee === 'string' && attendee === userId
        );
        return !isAlreadySelected && fullName.includes(searchTerm);
      });
      setFilteredUsers(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredUsers([]);
      setShowSuggestions(false);
    }
  }, [inputValue, users, selectedAttendees]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectUser = (user: any) => {
    const userId = user.user_id || user.user?.id || user.id;
    if (selectedAttendees.length < maxAttendees) {
      onAttendeesChange([...selectedAttendees, userId]);
    }
    setInputValue('');
    setShowSuggestions(false);
  };

  const handleCreateExternal = () => {
    if (!allowExternal || !inputValue.trim() || selectedAttendees.length >= maxAttendees) {
      return;
    }

    // Parse full name
    const nameParts = inputValue.trim().split(/\s+/);
    const first_name = nameParts[0] || '';
    const last_name = nameParts.slice(1).join(' ') || ''; // If only one word, last_name is empty

    const newUser: ExternalUser = {
      first_name,
      last_name: last_name || first_name // Use first_name as fallback for last_name if empty
    };

    onAttendeesChange([...selectedAttendees, newUser]);
    setInputValue('');
    setShowSuggestions(false);
  };

  const handleRemoveAttendee = (index: number) => {
    onAttendeesChange(selectedAttendees.filter((_, i) => i !== index));
  };

  const getAttendeeName = (attendee: string | ExternalUser): string => {
    if (typeof attendee === 'string') {
      const user = users.find(u => u.user_id === attendee || u.user?.id === attendee || u.id === attendee);
      if (user) {
        return `${user.user?.first_name || user.first_name} ${user.user?.last_name || user.last_name}`;
      }
      const globalUser = allUsers?.find(u => u.id === attendee);
      if (globalUser) {
        return `${globalUser.first_name} ${globalUser.last_name}`;
      }
      return 'Unknown User';
    }
    // For external users, show only first_name if last_name is the same (single word input)
    if (attendee.first_name === attendee.last_name) {
      return attendee.first_name;
    }
    return `${attendee.first_name} ${attendee.last_name}`;
  };

  const isExternalAttendee = (attendee: string | ExternalUser): boolean => {
    return typeof attendee === 'object';
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredUsers.length === 1) {
        // If only one suggestion, select it
        handleSelectUser(filteredUsers[0]);
      } else if (filteredUsers.length === 0 && allowExternal && inputValue.trim()) {
        // If no suggestions and external allowed, create new
        handleCreateExternal();
      }
    }
  };

  return (
    <div className="relative">
      {/* Selected Attendees */}
      {selectedAttendees.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {selectedAttendees.map((attendee, index) => (
            <div
              key={index}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                isExternalAttendee(attendee)
                  ? 'bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-800 border border-emerald-200'
                  : 'bg-[#f5f5f5] text-[#555] border border-[#eaeaea]'
              }`}
            >
              <span>{getAttendeeName(attendee)}</span>
              <button
                type="button"
                onClick={() => handleRemoveAttendee(index)}
                className="hover:bg-white/50 rounded-full p-0.5 transition-colors"
              >
                <FaTimes className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input Field */}
      {selectedAttendees.length < maxAttendees && (
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => inputValue && setShowSuggestions(true)}
            placeholder={placeholder}
            className="w-full px-3.5 py-2.5 text-sm border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0 focus:border-[#888] transition-all placeholder-[#aaa]"
          />

          {/* Suggestions Dropdown */}
          {showSuggestions && (inputValue.trim().length > 0) && (
            <div
              ref={dropdownRef}
              className="absolute z-50 w-full mt-1 bg-white border border-[#eaeaea] rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] max-h-60 overflow-y-auto"
            >
              {/* Existing users */}
              {filteredUsers.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-[#888] uppercase tracking-wide bg-[#fafafa]">
                    Project Team
                  </div>
                  {filteredUsers.map((user) => {
                    const userId = user.user_id || user.user?.id || user.id;
                    const fullName = `${user.user?.first_name || user.first_name} ${user.user?.last_name || user.last_name}`;
                    return (
                      <button
                        key={userId}
                        type="button"
                        onClick={() => handleSelectUser(user)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#f5f5f5] transition-colors text-left"
                      >
                        <div className="w-8 h-8 rounded-full bg-[#f5f5f5] flex items-center justify-center flex-shrink-0">
                          <FaUser className="w-3.5 h-3.5 text-[#171717]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-[#171717] truncate">
                            {fullName}
                          </div>
                          {user.user?.email && (
                            <div className="text-xs text-[#888] truncate">{user.user.email}</div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Create new option */}
              {allowExternal && filteredUsers.length === 0 && inputValue.trim().length > 0 && (
                <button
                  type="button"
                  onClick={handleCreateExternal}
                  className="w-full flex items-center gap-3 px-3 py-3 hover:bg-emerald-50 transition-colors text-left border-t border-[#f5f5f5]"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                    <FaPlus className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[#171717]">
                      Add "{inputValue}"
                    </div>
                    <div className="text-xs text-[#888]">Create as external contact</div>
                  </div>
                </button>
              )}

              {/* No results */}
              {!allowExternal && filteredUsers.length === 0 && (
                <div className="px-3 py-4 text-sm text-[#888] text-center">
                  No team members found
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Counter */}
      <div className="mt-2 text-xs text-[#888]">
        {selectedAttendees.length} / {maxAttendees} selected
      </div>
    </div>
  );
};

